import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import BiotechRoundedIcon from "@mui/icons-material/BiotechRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import EggAltRoundedIcon from "@mui/icons-material/EggAltRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import CoronavirusRoundedIcon from "@mui/icons-material/CoronavirusRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { deleteStudyPlanCall, listStudyPlansCall, listStudyPodcastsCall } from "../../redux/api/studyPlan.api";
import { STUDY_SUBJECTS, SUBJECT_LOOK } from "./data";
import StudyShell from "./StudyShell";

const SUBJECT_ICONS = {
  Histology: BiotechRoundedIcon,
  Anatomy: AccessibilityNewRoundedIcon,
  Embryology: EggAltRoundedIcon,
  Physiology: MonitorHeartRoundedIcon,
  Pathology: CoronavirusRoundedIcon,
  Pharmacology: MedicationRoundedIcon,
  Microbiology: BugReportRoundedIcon,
  Biochemistry: ScienceRoundedIcon,
  Immunology: HealthAndSafetyRoundedIcon,
  Neuroscience: PsychologyRoundedIcon,
  Histopathology: PaletteRoundedIcon,
  USMLE: SchoolRoundedIcon,
};

function whenLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function PlanCard({ plan, onOpen, onDelete, deleting }) {
  const progress = Math.max(0, Math.min(100, Number(plan.progress) || 0));
  return (
    <article className="study-card">
      <div className="study-card-top">
        <div>
          <div className="study-meta">{plan.goal === "exam" ? "Exam" : plan.goal === "mastery" ? "Mastery" : "General"}</div>
          <h3>{plan.title || plan.subject}</h3>
        </div>
        <div className="study-meta">{progress}%</div>
      </div>
      <div className="study-meta">
        {plan.topicsDone || 0} of {plan.topicsTotal || 0} topics
        {plan.updatedAt ? ` · ${whenLabel(plan.updatedAt)}` : ""}
      </div>
      <div className="study-bar" aria-hidden>
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="study-card-actions">
        <button type="button" className="go" onClick={() => onOpen(plan)}>
          Continue
        </button>
        {plan.status === "active" ? (
          <button type="button" disabled={deleting} onClick={() => onDelete(plan)}>
            {deleting ? "…" : "Delete"}
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function StudyHubHome({ showAll = false }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([listStudyPlansCall(), listStudyPodcastsCall()]).then(([planRes, podcastRes]) => {
      if (!alive) return;
      setPlans(Array.isArray(planRes?.plans) ? planRes.plans : []);
      setPodcasts(Array.isArray(podcastRes?.podcasts) ? podcastRes.podcasts : []);
      setError(planRes?.message && !planRes?.plans ? planRes.message : "");
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const visible = useMemo(() => {
    const sorted = [...plans].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    return showAll ? sorted : sorted.slice(0, 3);
  }, [plans, showAll]);

  const remove = async (plan) => {
    const ok = window.confirm(`Delete “${plan.title || plan.subject}”? Lessons in this plan will be removed.`);
    if (!ok) return;
    setDeletingId(plan.id);
    const response = await deleteStudyPlanCall(plan.id);
    setDeletingId("");
    if (response?.code) {
      setError(response.message || "This study could not be deleted.");
      return;
    }
    setPlans((current) => current.filter((item) => item.id !== plan.id));
  };

  return (
    <StudyShell>
      <section className="study-hero is-compact">
        <div className="study-kicker">Study Hub</div>
        <h1>Your next lesson is ready.</h1>
        <p>
          Build a study plan for histology, anatomy, embryology, and the rest of the course.
          Lessons, checks, flashcards, and a short quiz stay with your account.
        </p>
        <div className="study-hero-actions">
          <button type="button" className="study-primary" onClick={() => navigate("/study/new")}>
            Create a study plan
          </button>
          {!showAll && plans.length > 3 ? (
            <button type="button" className="study-ghost" onClick={() => navigate("/study/all")}>
              View all plans
            </button>
          ) : null}
        </div>
      </section>

      <div className="study-section">
        <h2>{showAll ? "All study plans" : "Continue studying"}</h2>
        {showAll ? (
          <button type="button" onClick={() => navigate("/study")}>Back</button>
        ) : null}
      </div>

      {loading ? <CircularProgress size={28} /> : null}
      {error ? <div className="study-error">{error}</div> : null}
      {!loading && !visible.length ? (
        <p className="study-meta">No study plan yet. Pick a subject below and start one.</p>
      ) : null}
      <div className="study-grid">
        {visible.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            deleting={deletingId === plan.id}
            onOpen={(item) => navigate(`/study/plan/${item.id}`)}
            onDelete={remove}
          />
        ))}
      </div>

      <div className="study-section">
        <h2>Popular subjects</h2>
      </div>
      <div className="study-subjects">
        {STUDY_SUBJECTS.map((subject) => {
          const Icon = SUBJECT_ICONS[subject] || BiotechRoundedIcon;
          const look = SUBJECT_LOOK[subject] || { color: "#2563eb", soft: "#dbeafe" };
          return (
            <button key={subject} type="button" onClick={() => navigate(`/study/new?subject=${encodeURIComponent(subject)}`)}>
              <span className="study-subject-icon" style={{ color: look.color, background: look.soft }}>
                <Icon />
              </span>
              <span>{subject}</span>
            </button>
          );
        })}
      </div>

      {podcasts.length ? (
        <>
          <div className="study-section">
            <h2>Podcasts</h2>
          </div>
          <div className="study-grid">
            {podcasts.slice(0, showAll ? podcasts.length : 3).map((item) => (
              <article className="study-card" key={item.lessonId}>
                <h3>{item.title || item.topicTitle}</h3>
                <div className="study-meta">{item.planTitle || item.topicTitle}</div>
                {item.url ? <audio controls src={item.url} style={{ width: "100%" }} /> : null}
              </article>
            ))}
          </div>
        </>
      ) : null}
    </StudyShell>
  );
}
